import React, { useState, useRef, useEffect } from "react";
import PdfViewer from "../pdf-viewer";

interface Position {
  x: number;
  y: number;
}

interface ImageModalViewerProps {
  image: string;
  onClose: () => void;
}

interface ImageContainerProps {
  image: string;
  extension: string;
}

const ImageModalViewer: React.FC<ImageModalViewerProps> = ({
  image,
  onClose,
}) => {
  const [scale, setScale] = useState<number>(1);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const lastMousePos = useRef<Position>({ x: 0, y: 0 });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const delta = e.deltaY * -0.005;
    const newScale = Math.min(Math.max(1, scale + delta), 4);

    if (newScale === scale) return;

    setScale(newScale);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === containerRef.current) {
      onClose();
      return;
    }
    if (scale === 1) return;

    e.preventDefault();
    setIsDragging(true);
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || scale === 1) return;

    const deltaX = e.clientX - lastMousePos.current.x;
    const deltaY = e.clientY - lastMousePos.current.y;

    setPosition((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));

    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDoubleClick = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
      onMouseDown={handleMouseDown}
    >
      <button
        className="absolute top-4 right-4 z-50 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        X
      </button>

      <div
        className="relative"
        style={{
          cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          maxWidth: "90vw",
          maxHeight: "90vh",
        }}
        onWheel={handleWheel}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      >
        <img
          ref={imageRef}
          src={image}
          alt="Modal view"
          className="select-none object-contain"
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transition: isDragging ? "none" : "transform 0.1s",
            maxWidth: "90vw",
            maxHeight: "90vh",
          }}
          draggable="false"
        />
      </div>
    </div>
  );
};

const ImageContainer: React.FC<ImageContainerProps> = ({
  image,
  extension,
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);

  if (extension === "pdf") {
    return <PdfViewer pdfUrl={image} width="70%" height="400px" />;
  }

  return (
    <>
      <div
        className="w-full h-72 rounded-xl-10 mr-4 mb-4 overflow-hidden cursor-pointer"
        onClick={() => setShowModal(true)}
      >
        <img
          loading="lazy"
          className="object-contain w-auto h-full mx-auto rounded-xl-10"
          src={image}
          alt="attachment"
        />
      </div>
      {showModal && (
        <ImageModalViewer image={image} onClose={() => setShowModal(false)} />
      )}
    </>
  );
};

export { ImageContainer };
